import { SubscriptionChargePreview } from '@easy-expense/data-schema-v2';
import { getTranslation } from '@easy-expense/intl-client';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

import { Button } from '../../Button.components';
import { Modal } from '../../Shared/Modal.component';

type PreviewState = 'loading' | 'error' | SubscriptionChargePreview;

/** Grey summary box mirroring the seat-selection modal's total row. */
const PreviewContainer: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Layout.Column bg="grayXXLight" radius={8} px={16} py={12}>
    {children}
  </Layout.Column>
);

/**
 * Confirm/dismiss modal styled like the seat-selection modal, used in place of
 * `window.confirm` for billing actions that need an explicit confirmation. When
 * `getPreview` is provided it fetches and shows the prorated charge/credit the
 * customer will see before they confirm.
 */
export const ConfirmationModal: React.FC<{
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  dismissLabel?: string;
  destructive?: boolean;
  getPreview?: () => Promise<SubscriptionChargePreview>;
}> = ({
  showModal,
  setShowModal,
  title,
  message,
  confirmLabel,
  onConfirm,
  dismissLabel,
  destructive,
  getPreview,
}) => {
  const [preview, setPreview] = React.useState<PreviewState | null>(getPreview ? 'loading' : null);

  React.useEffect(() => {
    if (!getPreview) {
      return;
    }
    let cancelled = false;
    getPreview()
      .then((result) => {
        if (!cancelled) {
          setPreview(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreview('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [getPreview]);

  const confirm = () => {
    onConfirm();
    setShowModal(false);
  };

  // Selects what goes inside the grey preview box by state. Returns null when
  // there's nothing to preview (e.g. the cancel modal). A charge/credit result
  // shows an amount row; every other state shows a single one-line note.
  const previewBox = () => {
    if (!preview) {
      return null;
    }
    if (preview !== 'loading' && preview !== 'error') {
      if (preview.action === 'charge' || preview.action === 'credit') {
        const label =
          preview.action === 'charge'
            ? getTranslation('Due today')
            : getTranslation('Account credit');
        return (
          <PreviewContainer>
            <Layout.Row justify="space-between" align>
              <OpenSans.Primary size="s-16">{label}</OpenSans.Primary>
              <OpenSans.Primary size="s-16" weight="bold-700">
                {preview.amountFormatted}
              </OpenSans.Primary>
            </Layout.Row>
          </PreviewContainer>
        );
      }
    }

    // The 'error' note is for a genuine preview failure (network / server) — the
    // expected "settles next invoice" case is action 'deferred', kept distinct.
    let note = getTranslation('No charge for this change.');
    if (preview === 'loading') {
      note = getTranslation('Calculating…');
    } else if (preview === 'error') {
      note = getTranslation(
        "We couldn't load the estimated amount. You can continue — any charge or credit will apply to your account.",
      );
    } else if (preview.action === 'deferred') {
      note = getTranslation(
        'This change applies now; any adjustment appears on your next invoice.',
      );
    } else if (preview.action === 'trial') {
      note = getTranslation("You won't be charged until your trial ends.");
    }
    return (
      <PreviewContainer>
        <OpenSans.Secondary size="s-16">{note}</OpenSans.Secondary>
      </PreviewContainer>
    );
  };

  const box = previewBox();

  // Don't let the user confirm before the previewed charge is shown — gate the
  // click, relabel, and dim the button while the preview loads.
  const isPreviewLoading = preview === 'loading';
  const confirmButtonLabel = isPreviewLoading ? getTranslation('Calculating…') : confirmLabel;
  const ConfirmButton = destructive ? Button.Destructive : Button.Primary;

  return (
    <Modal showModal={showModal} setShowModal={setShowModal}>
      <Layout.Column style={{ minWidth: 360, maxWidth: 420 }}>
        <OpenSans.Primary size="xl-28" weight="bold-700">
          {title}
        </OpenSans.Primary>

        <Spacer.Vertical size={12} />

        <OpenSans.Secondary size="s-16">{message}</OpenSans.Secondary>

        {box ? (
          <>
            <Spacer.Vertical size={16} />
            {box}
          </>
        ) : null}

        <Spacer.Vertical size={24} />

        <Layout.Row>
          <Button.Secondary
            content={dismissLabel ?? getTranslation('Cancel')}
            onClick={() => setShowModal(false)}
          />
          <Spacer.Horizontal size={12} />
          <Layout.Column style={isPreviewLoading ? { opacity: 0.55 } : undefined}>
            <ConfirmButton
              content={confirmButtonLabel}
              active={!isPreviewLoading}
              onClick={confirm}
            />
          </Layout.Column>
        </Layout.Row>
      </Layout.Column>
    </Modal>
  );
};
